import { Check, ImagePlus, Layout, Palette, ShoppingBag, Upload, X } from 'lucide-react'
import { type ReactNode, useEffect, useState } from 'react'

import type { Plantilla } from '../../api/alta'
import type { Analisis, Mapeo } from '../../api/importacion'
import type { TipoDeWeb } from '../../api/tipos'
import { Aviso, Campo, Esqueleto, Selector, clases } from '../../componentes/ui'
import { marca } from '../../marca/marca'
import { DOMINIO_DE_LA_PLATAFORMA } from '../../marco/secciones'
import { creditosEnLlano } from '../consumo'
import { ComoQuedaran, SelectorDeArchivo, TablaDeMapeo } from '../Importar'
import { coloresDelLogo, type Paleta } from './colores'
import { altDe, type Datos, type Ficheros, type Imagen, MAXIMO_DE_DOCUMENTOS, MAXIMO_DE_IMAGENES_SUELTAS, MONEDAS, slugDe, TIPOGRAFIAS } from './estado'

/**
 * Las pantallas del asistente (docs/alta-de-web-con-plantillas.md §8), una por paso.
 * Ninguna decide nada que el núcleo no vuelva a decidir: qué plan admite tienda, qué
 * plantilla vale, cuántas webs caben. Aquí solo se recoge y se enseña.
 */
type Props = { datos: Datos; cambiar: (parte: Partial<Datos>) => void }

/**
 * La miniatura de un fichero que el cliente acaba de elegir. La URL se crea **una vez
 * por fichero** y se libera al desmontar: escrita en el JSX se creaba una nueva en cada
 * render —o sea, en cada tecla del texto alternativo—, el navegador volvía a decodificar
 * todas las miniaturas y ninguna de esas URL se soltaba mientras viviera la pestaña.
 */
const Miniatura = ({ fichero, className }: { className: string; fichero: File }) => {
  const [url, setUrl] = useState('')
  useEffect(() => {
    const creada = URL.createObjectURL(fichero)
    setUrl(creada)
    return () => URL.revokeObjectURL(creada)
  }, [fichero])
  return url ? <img alt="" className={className} src={url} /> : <span className={className} />
}

const Seccion = ({ titulo, descripcion, children }: { children: ReactNode; descripcion?: ReactNode; titulo: ReactNode }) => (
  <section className="rounded-caja border border-linea bg-papel p-5 md:p-6">
    <h2 className="font-display text-base font-semibold">{titulo}</h2>
    {descripcion && <p className="mt-1 text-[13px] text-tenue">{descripcion}</p>}
    <div className="mt-4">{children}</div>
  </section>
)

const TarjetaElegible = ({ elegida, alElegir, deshabilitada, children, className }: { alElegir: () => void; children: ReactNode; className?: string; deshabilitada?: boolean; elegida: boolean }) => (
  <button
    aria-pressed={elegida}
    className={clases(
      'relative flex w-full flex-col items-start rounded-caja border-2 bg-papel p-5 text-left transition-colors',
      elegida ? 'border-violeta bg-violeta-suave/40' : 'border-linea hover:border-linea-fuerte',
      deshabilitada && 'cursor-not-allowed opacity-60',
      className,
    )}
    disabled={deshabilitada}
    onClick={alElegir}
    type="button"
  >
    {elegida && (
      <span className="absolute top-3 right-3 flex size-6 items-center justify-center rounded-full bg-violeta text-white">
        <Check className="size-3.5" />
      </span>
    )}
    {children}
  </button>
)

// --- 1. Tipo -------------------------------------------------------------------------

export const PasoTipo = ({ datos, cambiar, planConTienda }: Props & { planConTienda: boolean }) => (
  <Seccion descripcion="El tipo decide qué módulos tiene la web y qué plantillas puedes elegir. Una informativa puede convertirse en tienda más adelante." titulo="¿Qué tipo de web quieres?">
    <div className="grid gap-4 md:grid-cols-2">
      <TarjetaElegible alElegir={() => cambiar({ tipo: 'informativa', plantilla: null, paginas: [] })} elegida={datos.tipo === 'informativa'}>
        <Layout className="mb-3 size-7 text-violeta" />
        <span className="font-display text-base font-semibold">Web informativa</span>
        <span className="mt-1 text-[13px] text-tenue">Servicios, empresa, estudio, consulta. Páginas, formulario de contacto, blog y SEO.</span>
      </TarjetaElegible>
      <TarjetaElegible alElegir={() => cambiar({ tipo: 'tienda', plantilla: null, paginas: [] })} deshabilitada={!planConTienda} elegida={datos.tipo === 'tienda'}>
        <ShoppingBag className="mb-3 size-7 text-violeta" />
        <span className="font-display text-base font-semibold">Tienda</span>
        <span className="mt-1 text-[13px] text-tenue">Catálogo de productos con carrito y pago en línea, o solo catálogo con «Consultar». Más todo lo de la informativa.</span>
        {!planConTienda && <span className="mt-3 rounded-pastilla border border-aviso/40 bg-aviso-suave px-2 py-0.5 text-xs font-medium text-aviso">Tu plan no incluye tienda: sube de plan para elegirla</span>}
      </TarjetaElegible>
    </div>
  </Seccion>
)

// --- 2. Plantilla ----------------------------------------------------------------------

const ROTULO_DE_CAPTURA: Record<string, string> = { portada: 'Portada', movil: 'En el móvil', interior: 'Una página interior' }

/** «Ver más»: las otras capturas (móvil, interior), que son de la plantilla construida con contenido de muestra, no maquetas. */
const MasCapturas = ({ p }: { p: Plantilla }) => {
  const [abiertas, setAbiertas] = useState(false)
  const otras = p.capturas.filter((c) => c !== 'portada')
  if (otras.length === 0) return null
  return (
    <div className="mt-2">
      <button className="text-xs font-medium text-violeta hover:underline" onClick={() => setAbiertas((v) => !v)} type="button">
        {abiertas ? 'Ver menos' : 'Ver más capturas'}
      </button>
      {abiertas && (
        <div className="mt-2 grid grid-cols-2 gap-2">
          {otras.map((c) => (
            <figure key={c}>
              <img alt={`${ROTULO_DE_CAPTURA[c] ?? c} de la plantilla ${p.nombre}`} className="max-h-72 w-full rounded-lg border border-linea bg-papel-2 object-cover object-top" src={`/api/panel/plantillas/captura?plantilla=${p.slug}&nombre=${c}`} />
              <figcaption className="mt-1 text-xs text-tenue">{ROTULO_DE_CAPTURA[c] ?? c}</figcaption>
            </figure>
          ))}
        </div>
      )}
    </div>
  )
}

export const PasoPlantilla = ({ datos, cambiar, plantillas, cargando }: Props & { cargando: boolean; plantillas: Plantilla[] }) => (
  <Seccion descripcion="La plantilla decide la estructura y el carácter de la web. Tu logo, tus colores, tus fotos y tus textos son lo que se pinta con ese carácter." titulo="Elige una plantilla">
    {cargando ? (
      <Esqueleto filas={3} />
    ) : plantillas.length === 0 ? (
      <Aviso tono="info">Todavía no hay plantillas para este tipo de web.</Aviso>
    ) : (
      <div className="grid gap-4 md:grid-cols-2">
        {plantillas.map((p) => (
          <div key={p.slug}>
            <TarjetaElegible alElegir={() => cambiar({ plantilla: p.slug, paginas: [] })} elegida={datos.plantilla === p.slug}>
              <div className="mb-3 flex h-36 w-full items-center justify-center overflow-hidden rounded-lg border border-linea bg-papel-2">
                {p.capturas.includes('portada') ? (
                  <img alt={`Portada de la plantilla ${p.nombre}`} className="h-full w-full object-cover object-top" src={`/api/panel/plantillas/captura?plantilla=${p.slug}&nombre=portada`} />
                ) : (
                  <span className="text-xs text-tenue">La captura llegará cuando la plantilla se construya con contenido de muestra.</span>
                )}
              </div>
              <span className="font-display text-base font-semibold">{p.nombre}</span>
              <span className="mt-1 text-[13px] text-tinta-2">{p.descripcion}</span>
              <span className="mt-3 flex flex-wrap gap-1.5">
                {p.etiquetas.map((e) => (
                  <span className="rounded-pastilla border border-linea bg-papel-2 px-2 py-0.5 text-xs text-tenue" key={e}>
                    {e}
                  </span>
                ))}
              </span>
              <span className="mt-3 text-xs text-tenue">
                Páginas: {p.paginas.map((x) => x.titulo).join(' · ')}
              </span>
            </TarjetaElegible>
            <MasCapturas p={p} />
          </div>
        ))}
      </div>
    )}
  </Seccion>
)

// --- 3. Marca --------------------------------------------------------------------------

const Color = ({ etiqueta, valor, alCambiar, ayuda }: { alCambiar: (v: string) => void; ayuda?: string; etiqueta: string; valor: string }) => (
  <label className="flex flex-col gap-1.5">
    <span className="text-[13px] font-medium">{etiqueta}</span>
    <span className="flex items-center gap-2">
      <input aria-label={`${etiqueta} (selector)`} className="size-9 shrink-0 cursor-pointer rounded-campo border border-linea-fuerte bg-papel p-0.5" onChange={(e) => alCambiar(e.target.value)} type="color" value={/^#[0-9a-f]{6}$/i.test(valor) ? valor : '#888888'} />
      <input className="campo w-full rounded-campo border border-linea-fuerte bg-papel px-3 py-2 text-sm" onChange={(e) => alCambiar(e.target.value.trim())} placeholder="#1e3a8a" value={valor} />
    </span>
    {ayuda && <span className="text-xs text-tenue">{ayuda}</span>}
  </label>
)

const SlotDeImagen = ({ etiqueta, ayuda, imagen, alCambiar }: { alCambiar: (i: Imagen | null) => void; ayuda?: string; etiqueta: string; imagen: Imagen | null }) => (
  <div className="flex flex-col gap-2 rounded-caja border border-linea bg-papel-2 p-3">
    <span className="text-[13px] font-medium">{etiqueta}</span>
    {imagen ? (
      <div className="flex items-center gap-3">
        <Miniatura className="size-14 rounded-lg border border-linea bg-papel object-contain" fichero={imagen.fichero} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px]">{imagen.fichero.name}</p>
          <input aria-label={`Texto alternativo de ${etiqueta}`} className="campo mt-1 w-full rounded-campo border border-linea-fuerte bg-papel px-2 py-1 text-[13px]" onChange={(e) => alCambiar({ ...imagen, alt: e.target.value })} placeholder="Qué se ve en la imagen" value={imagen.alt} />
        </div>
        <button aria-label={`Quitar ${etiqueta}`} className="rounded-boton p-1.5 text-tenue hover:bg-papel hover:text-error" onClick={() => alCambiar(null)} type="button">
          <X className="size-4" />
        </button>
      </div>
    ) : (
      <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-linea-fuerte bg-papel px-3 py-4 text-[13px] text-tenue hover:border-violeta">
        <Upload className="size-4" /> Elegir imagen
        <input accept="image/jpeg,image/png,image/webp,image/avif,image/gif" className="sr-only" onChange={(e) => e.target.files?.[0] && alCambiar({ fichero: e.target.files[0], alt: altDe(e.target.files[0]) })} type="file" />
      </label>
    )}
    {ayuda && <span className="text-xs text-tenue">{ayuda}</span>}
  </div>
)

export const PasoMarca = ({ datos, cambiar, ficheros, cambiarFicheros }: Props & { cambiarFicheros: (parte: Partial<Ficheros>) => void; ficheros: Ficheros }) => {
  // Los colores que salen del logotipo, como propuesta: se calculan aquí, en el navegador, y se aceptan con un botón.
  const [paleta, setPaleta] = useState<Paleta | null>(null)
  const logo = ficheros.logo?.fichero ?? null
  useEffect(() => {
    let vigente = true
    setPaleta(null)
    if (!logo) return
    void coloresDelLogo(logo).then((p) => {
      if (vigente) setPaleta(p)
    })
    return () => {
      vigente = false
    }
  }, [logo])
  const yaPuestos = paleta !== null && datos.marca.primario === paleta.primario && datos.marca.secundario === paleta.secundario && datos.marca.acento === paleta.acento

  return (
  <div className="flex flex-col gap-4">
    <Seccion descripcion="Cómo se llama la web y en qué dirección se sirve. El subdominio de la plataforma es tuyo desde el primer día; el dominio propio se puede poner ahora o después." titulo="Nombre y dirección">
      <div className="grid gap-4 md:grid-cols-2">
        <Campo
          etiqueta="Nombre de la web"
          onChange={(e) => cambiar({ nombre: e.target.value, ...(datos.slugTocado ? {} : { slug: slugDe(e.target.value) }) })}
          placeholder="Ferretería El Sol"
          value={datos.nombre}
        />
        <Campo
          ayuda={datos.slug ? `Se servirá en ${datos.slug}.${marca()?.dominioWebs ?? DOMINIO_DE_LA_PLATAFORMA}` : 'Minúsculas, números y guiones.'}
          etiqueta="Dirección en la plataforma"
          onChange={(e) => cambiar({ slug: slugDe(e.target.value), slugTocado: true })}
          placeholder="ferreteria-el-sol"
          value={datos.slug}
        />
        <Campo ayuda="Opcional. Solo el dominio, sin «https://». Lo podrás conectar más tarde desde Mis sitios." etiqueta="Dominio propio" onChange={(e) => cambiar({ dominio: e.target.value.trim().toLowerCase() })} placeholder="ferreteriaelsol.pe" value={datos.dominio} />
      </div>
    </Seccion>

    <Seccion descripcion="El logo va en la cabecera; el de fondo oscuro, en el pie; el icono, en la pestaña del navegador. Todos son opcionales y se cambian en Ajustes." titulo="Logotipo">
      <div className="grid gap-3 md:grid-cols-3">
        <SlotDeImagen alCambiar={(i) => cambiarFicheros({ logo: i })} etiqueta="Logotipo" imagen={ficheros.logo} />
        <SlotDeImagen alCambiar={(i) => cambiarFicheros({ logoEnOscuro: i })} etiqueta="Logotipo para fondos oscuros" imagen={ficheros.logoEnOscuro} />
        <SlotDeImagen alCambiar={(i) => cambiarFicheros({ favicon: i })} ayuda="Cuadrado, de 512 px o más." etiqueta="Icono del sitio" imagen={ficheros.favicon} />
      </div>
    </Seccion>

    <Seccion descripcion="Los colores de tu marca como dato: cómo se usan lo decide la plantilla. Si los dejas vacíos, se usan los de la plantilla." titulo="Colores y tipografía">
      {paleta && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-caja border border-linea bg-papel-2 p-3" role="status">
          <Palette className="size-4 shrink-0 text-violeta" />
          <span className="text-[13px]">Del logotipo salen estos colores:</span>
          <span className="flex items-center gap-1.5">
            {[paleta.primario, paleta.secundario, paleta.acento].map((c, i) => (
              <span aria-label={`${['Primario', 'Secundario', 'Acento'][i]} propuesto ${c}`} className="inline-block size-6 rounded-full border border-linea-fuerte" key={`${c}-${i}`} style={{ background: c }} title={c} />
            ))}
          </span>
          {yaPuestos ? (
            <span className="text-[13px] text-tenue">Puestos.</span>
          ) : (
            <button className="text-[13px] font-medium text-violeta hover:underline" onClick={() => cambiar({ marca: { ...datos.marca, primario: paleta.primario, secundario: paleta.secundario, acento: paleta.acento } })} type="button">
              Usar estos colores
            </button>
          )}
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-3">
        <Color alCambiar={(v) => cambiar({ marca: { ...datos.marca, primario: v } })} ayuda="Botones, enlaces, títulos." etiqueta="Primario" valor={datos.marca.primario} />
        <Color alCambiar={(v) => cambiar({ marca: { ...datos.marca, secundario: v } })} etiqueta="Secundario" valor={datos.marca.secundario} />
        <Color alCambiar={(v) => cambiar({ marca: { ...datos.marca, acento: v } })} ayuda="Avisos, insignias, ofertas." etiqueta="Acento" valor={datos.marca.acento} />
      </div>
      <div className="mt-5 grid gap-3 md:grid-cols-3">
        {TIPOGRAFIAS.map((t) => (
          <TarjetaElegible alElegir={() => cambiar({ marca: { ...datos.marca, tipografia: t.valor } })} className="!p-4" elegida={datos.marca.tipografia === t.valor} key={t.valor}>
            <span className={clases('text-2xl font-semibold', t.muestra)}>Aa</span>
            <span className="mt-2 text-[13px] font-semibold">{t.nombre}</span>
            <span className="text-xs text-tenue">{t.descripcion}</span>
          </TarjetaElegible>
        ))}
      </div>
    </Seccion>

    <Seccion descripcion="Van en el pie, en la página de contacto y, si tienes tienda, en los avisos de pedidos. Se cambian en Ajustes." titulo="Contacto">
      <div className="grid gap-4 md:grid-cols-2">
        <Campo etiqueta="Correo de contacto" onChange={(e) => cambiar({ contacto: { ...datos.contacto, correo: e.target.value.trim() } })} placeholder="hola@ferreteriaelsol.pe" type="email" value={datos.contacto.correo} />
        <Campo ayuda="Con prefijo: «+51 999 888 777». Es el que usa «Consultar por WhatsApp»." etiqueta="Teléfono" onChange={(e) => cambiar({ contacto: { ...datos.contacto, telefono: e.target.value } })} placeholder="+51 999 888 777" type="tel" value={datos.contacto.telefono} />
        <Campo ayuda="Opcional. Sale en el pie y en la página de contacto." etiqueta="Dirección" onChange={(e) => cambiar({ contacto: { ...datos.contacto, direccion: e.target.value } })} placeholder="Av. Industrial 120, Lima" value={datos.contacto.direccion} />
        <label className="flex flex-col gap-1.5 md:col-span-2">
          <span className="text-[13px] font-medium text-tinta">Horario</span>
          <textarea
            className="campo w-full rounded-campo border border-linea bg-papel px-3 py-2 text-sm text-tinta placeholder:text-tenue hover:border-linea-fuerte"
            maxLength={500}
            onChange={(e) => cambiar({ contacto: { ...datos.contacto, horario: e.target.value } })}
            placeholder={'Lun a vie 9:00–19:00 · Sáb 9:00–14:00'}
            rows={2}
            value={datos.contacto.horario}
          />
          <span className="text-xs text-tenue">Opcional. Como lo dirías en la puerta; una línea por grupo de días si quieres. Las plantillas de locales lo enseñan arriba, junto a la dirección.</span>
        </label>
      </div>
    </Seccion>

    <Seccion descripcion="Son las variables de los términos y la política de privacidad, que se redactan con ellos. Opcionales: lo que falte queda marcado «por rellenar» en esas páginas." titulo="Datos legales">
      <div className="grid gap-4 md:grid-cols-3">
        <Campo etiqueta="Razón social" onChange={(e) => cambiar({ fiscal: { ...datos.fiscal, razonSocial: e.target.value } })} placeholder="Ferretería El Sol S.A.C." value={datos.fiscal.razonSocial} />
        <Campo etiqueta="Identificación fiscal (RUC, NIF…)" onChange={(e) => cambiar({ fiscal: { ...datos.fiscal, identificacion: e.target.value.trim() } })} placeholder="20601234567" value={datos.fiscal.identificacion} />
        <Campo etiqueta="Domicilio fiscal" onChange={(e) => cambiar({ fiscal: { ...datos.fiscal, direccion: e.target.value } })} placeholder="Av. Industrial 120, Lima" value={datos.fiscal.direccion} />
      </div>
    </Seccion>

    {datos.tipo === 'tienda' && (
      <Seccion descripcion="Es un interruptor: el día que quieras cobrar en línea, cambias aquí y configuras la pasarela; el catálogo no se toca." titulo="Cómo vendes">
        <div className="grid gap-4 md:grid-cols-2">
          <TarjetaElegible alElegir={() => cambiar({ tienda: { ...datos.tienda, modo: 'tienda' } })} className="!p-4" elegida={datos.tienda.modo === 'tienda'}>
            <span className="text-[13px] font-semibold">Tienda en línea</span>
            <span className="text-xs text-tenue">Carrito, pedido y pago con pasarela, o «te decimos cómo pagar».</span>
          </TarjetaElegible>
          <TarjetaElegible alElegir={() => cambiar({ tienda: { ...datos.tienda, modo: 'catalogo' } })} className="!p-4" elegida={datos.tienda.modo === 'catalogo'}>
            <span className="text-[13px] font-semibold">Catálogo</span>
            <span className="text-xs text-tenue">Se enseña el producto y el visitante consulta: por WhatsApp, por formulario o los dos.</span>
          </TarjetaElegible>
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-medium">Moneda</span>
            <Selector onChange={(e) => cambiar({ tienda: { ...datos.tienda, moneda: e.target.value } })} value={datos.tienda.moneda}>
              {MONEDAS.map((m) => (
                <option key={m.valor} value={m.valor}>
                  {m.nombre}
                </option>
              ))}
            </Selector>
          </label>
          {datos.tienda.modo === 'catalogo' && (
            <label className="flex flex-col gap-1.5">
              <span className="text-[13px] font-medium">A dónde lleva «Consultar»</span>
              <Selector onChange={(e) => cambiar({ tienda: { ...datos.tienda, consulta: e.target.value as Datos['tienda']['consulta'] } })} value={datos.tienda.consulta}>
                <option value="ambos">Los dos: WhatsApp primero y el formulario debajo</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="formulario">Formulario</option>
              </Selector>
              <span className="text-xs text-tenue">WhatsApp es inmediato pero no deja rastro en Respuestas; el formulario cae en Respuestas con su correo y su exportación, y funciona sin JavaScript.</span>
            </label>
          )}
        </div>
      </Seccion>
    )}
  </div>
  )
}

// --- 4. Materiales -----------------------------------------------------------------------

/** Las claves de imagen que la receta pide para las páginas que van a crearse. */
export const clavesDeImagen = (plantilla: Plantilla | undefined, paginasElegidas: string[]) => {
  if (!plantilla) return []
  const van = new Set(plantilla.paginas.filter((p) => p.obligatoria || paginasElegidas.includes(p.clave)).map((p) => p.clave))
  return plantilla.claves.filter((c) => (c.tipo === 'imagen' || c.tipo === 'imagenes') && c.paginas.some((p) => van.has(p)))
}

export const PasoMateriales = ({ datos, cambiar, ficheros, cambiarFicheros, plantilla }: Props & { cambiarFicheros: (parte: Partial<Ficheros>) => void; ficheros: Ficheros; plantilla: Plantilla | undefined }) => {
  const claves = clavesDeImagen(plantilla, datos.paginas)
  const poner = (clave: string, lista: Imagen[]) => cambiarFicheros({ imagenes: { ...ficheros.imagenes, [clave]: lista } })
  return (
    <div className="flex flex-col gap-4">
      <Seccion descripcion="Esto es lo que la plantilla necesita para las páginas que has elegido. Lo que no subas ahora se puede añadir después desde Medios. Cada imagen lleva un texto alternativo: lo leen quienes no la ven y los buscadores." titulo="Imágenes y banners">
        {claves.length === 0 ? (
          <Aviso tono="info">Esta plantilla no pide imágenes para las páginas elegidas. Puedes subir las que quieras después, desde Medios.</Aviso>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {claves.map((c) => {
              const lista = ficheros.imagenes[c.clave] ?? []
              const varias = c.tipo === 'imagenes'
              return (
                <div className="flex flex-col gap-2 rounded-caja border border-linea bg-papel-2 p-3" key={c.clave}>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[13px] font-medium">{c.rotulo}</span>
                    <span className="text-xs text-tenue">{varias ? 'Varias' : 'Una'} · {c.paginas.map((p) => plantilla?.paginas.find((x) => x.clave === p)?.titulo ?? p).join(', ')}</span>
                  </div>
                  {lista.map((img, i) => (
                    <div className="flex items-center gap-3" key={`${img.fichero.name}-${i}`}>
                      <Miniatura className="size-12 rounded-lg border border-linea bg-papel object-cover" fichero={img.fichero} />
                      <input aria-label={`Texto alternativo de ${img.fichero.name}`} className="campo min-w-0 flex-1 rounded-campo border border-linea-fuerte bg-papel px-2 py-1 text-[13px]" onChange={(e) => poner(c.clave, lista.map((x, j) => (j === i ? { ...x, alt: e.target.value } : x)))} placeholder="Qué se ve en la imagen" value={img.alt} />
                      <button aria-label={`Quitar ${img.fichero.name}`} className="rounded-boton p-1.5 text-tenue hover:bg-papel hover:text-error" onClick={() => poner(c.clave, lista.filter((_, j) => j !== i))} type="button">
                        <X className="size-4" />
                      </button>
                    </div>
                  ))}
                  {(varias || lista.length === 0) && (
                    <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-linea-fuerte bg-papel px-3 py-3 text-[13px] text-tenue hover:border-violeta">
                      <ImagePlus className="size-4" /> {varias ? 'Añadir imágenes' : 'Elegir imagen'}
                      <input
                        accept="image/jpeg,image/png,image/webp,image/avif,image/gif"
                        className="sr-only"
                        multiple={varias}
                        onChange={(e) => {
                          const nuevos = Array.from(e.target.files ?? []).map((f) => ({ fichero: f, alt: altDe(f) }))
                          poner(c.clave, varias ? [...lista, ...nuevos].slice(0, 30) : nuevos.slice(0, 1))
                          // El input sigue montado entre selecciones: sin esto, volver a elegir el
                          // mismo fichero que se acaba de quitar no dispara nada.
                          e.target.value = ''
                        }}
                        type="file"
                      />
                    </label>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </Seccion>
      <Seccion descripcion="Todas las que quieras, de una vez. Las colocamos en los huecos de la plantilla que se queden sin foto —la portada primero— y las demás quedan en tu biblioteca de Medios, con su texto alternativo. Si alguna es el logotipo de una marca con la que trabajas, que su nombre lo diga («logo-acme.png»)." titulo="Más imágenes">
        {ficheros.sueltas.length > 0 && (
          <div className="mb-3 grid gap-2 md:grid-cols-2">
            {ficheros.sueltas.map((img, i) => (
              <div className="flex items-center gap-3 rounded-caja border border-linea bg-papel-2 p-2" key={`${img.fichero.name}-${i}`}>
                <Miniatura className="size-12 rounded-lg border border-linea bg-papel object-cover" fichero={img.fichero} />
                <input aria-label={`Texto alternativo de ${img.fichero.name}`} className="campo min-w-0 flex-1 rounded-campo border border-linea-fuerte bg-papel px-2 py-1 text-[13px]" onChange={(e) => cambiarFicheros({ sueltas: ficheros.sueltas.map((x, j) => (j === i ? { ...x, alt: e.target.value } : x)) })} placeholder="Qué se ve en la imagen" value={img.alt} />
                <button aria-label={`Quitar ${img.fichero.name}`} className="rounded-boton p-1.5 text-tenue hover:bg-papel hover:text-error" onClick={() => cambiarFicheros({ sueltas: ficheros.sueltas.filter((_, j) => j !== i) })} type="button">
                  <X className="size-4" />
                </button>
              </div>
            ))}
          </div>
        )}
        {ficheros.sueltas.length < MAXIMO_DE_IMAGENES_SUELTAS && (
          <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-linea-fuerte bg-papel px-3 py-3 text-[13px] text-tenue hover:border-violeta">
            <ImagePlus className="size-4" /> Añadir varias imágenes
            <input
              accept="image/jpeg,image/png,image/webp,image/avif,image/gif"
              className="sr-only"
              multiple
              onChange={(e) => {
                const nuevas = Array.from(e.target.files ?? []).map((f) => ({ fichero: f, alt: altDe(f) }))
                cambiarFicheros({ sueltas: [...ficheros.sueltas, ...nuevas].slice(0, MAXIMO_DE_IMAGENES_SUELTAS) })
                e.target.value = ''
              }}
              type="file"
            />
          </label>
        )}
        <p className="mt-2 text-xs text-tenue">
          Hasta {MAXIMO_DE_IMAGENES_SUELTAS}. Se suben varias a la vez al crear la web{ficheros.sueltas.length > 0 ? ` (${ficheros.sueltas.length} por ahora)` : ''}.
        </p>
      </Seccion>
      <Seccion descripcion="Qué haces, para quién y qué te diferencia. Con esto se redactan los textos de tus páginas: lo que no cuentes aquí no se inventa, se deja por rellenar para que lo escribas tú." titulo="Cuéntanos tu negocio">
        <textarea
          className="campo w-full rounded-campo border border-linea-fuerte bg-papel px-3 py-2 text-sm"
          maxLength={5000}
          onChange={(e) => cambiar({ brief: e.target.value })}
          placeholder="Somos una ferretería con veinte años en Lima Norte. Vendemos herramientas y material eléctrico, atendemos a particulares y a contratistas, y entregamos el mismo día en la zona."
          rows={6}
          value={datos.brief}
        />
        <div className="mt-4">
          <p className="text-[13px] font-medium">¿Tienes un folleto, una presentación o un documento con lo que ofreces?</p>
          <p className="mb-2 text-xs text-tenue">PDF, Word (.docx) o texto, hasta {MAXIMO_DE_DOCUMENTOS}. Se lee su texto para redactar; el fichero no se publica.</p>
          {ficheros.documentos.length > 0 && (
            <ul className="mb-2 flex flex-col gap-1.5">
              {ficheros.documentos.map((d, i) => (
                <li className="flex items-center justify-between gap-3 rounded-caja border border-linea bg-papel-2 px-3 py-2 text-[13px]" key={`${d.name}-${i}`}>
                  <span className="truncate">{d.name}</span>
                  <button aria-label={`Quitar ${d.name}`} className="rounded-boton p-1 text-tenue hover:bg-papel hover:text-error" onClick={() => cambiarFicheros({ documentos: ficheros.documentos.filter((_, j) => j !== i) })} type="button">
                    <X className="size-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {ficheros.documentos.length < MAXIMO_DE_DOCUMENTOS && (
            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-linea-fuerte bg-papel px-3 py-3 text-[13px] text-tenue hover:border-violeta">
              <Upload className="size-4" /> Añadir un documento
              <input
                accept=".pdf,.docx,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown"
                className="sr-only"
                onChange={(e) => {
                  const nuevos = Array.from(e.target.files ?? [])
                  cambiarFicheros({ documentos: [...ficheros.documentos, ...nuevos].slice(0, MAXIMO_DE_DOCUMENTOS) })
                  e.target.value = ''
                }}
                type="file"
              />
            </label>
          )}
        </div>
      </Seccion>
    </div>
  )
}

// --- 5. Páginas ----------------------------------------------------------------------------

export const PasoPaginas = ({ datos, cambiar, plantilla }: Props & { plantilla: Plantilla | undefined }) => {
  if (!plantilla) return <Aviso tono="aviso">Elige primero una plantilla.</Aviso>
  const conServicios = plantilla.paginas.some((p) => p.clave === 'servicios')
  const marcar = (clave: string, si: boolean) => cambiar({ paginas: si ? [...new Set([...datos.paginas, clave])] : datos.paginas.filter((c) => c !== clave) })
  return (
    <div className="flex flex-col gap-4">
      <Seccion descripcion="Las que trae la plantilla. Las obligatorias van siempre; las demás, si las marcas. Todas nacen como borradores con su estructura, para que las revises antes de publicar." titulo="¿Qué páginas quieres?">
        <ul className="grid gap-2 md:grid-cols-2">
          {plantilla.paginas.map((p) => {
            const marcada = p.obligatoria || datos.paginas.includes(p.clave)
            return (
              <li key={p.clave}>
                <label className={clases('flex items-start gap-3 rounded-caja border p-3', marcada ? 'border-violeta-borde bg-violeta-suave/40' : 'border-linea bg-papel', p.obligatoria && 'opacity-80')}>
                  <input checked={marcada} className="mt-0.5 size-4 accent-violeta" disabled={p.obligatoria} onChange={(e) => marcar(p.clave, e.target.checked)} type="checkbox" />
                  <span className="min-w-0">
                    <span className="block text-[13px] font-medium">{p.titulo}</span>
                    <span className="block text-xs text-tenue">{p.legal ? 'Legal · obligatoria' : p.obligatoria ? 'Obligatoria' : 'Opcional'}</span>
                  </span>
                </label>
              </li>
            )
          })}
        </ul>
      </Seccion>
      {conServicios && (
        <Seccion descripcion="Uno por línea. Cada uno tendrá su propia página, colgando de «Servicios», con el nombre que escribas y el resto por rellenar." titulo="Tus servicios">
          <textarea className="campo w-full rounded-campo border border-linea-fuerte bg-papel px-3 py-2 text-sm" onChange={(e) => cambiar({ servicios: e.target.value })} placeholder={'Diseño de interiores\nReformas integrales\nAsesoría'} rows={5} value={datos.servicios} />
        </Seccion>
      )}
    </div>
  )
}

// --- 6. Productos (tienda) -----------------------------------------------------------------

export type ProductosDelAlta = { archivo: File; analisis: Analisis; mapeo: Mapeo[] }

export const PasoProductos = ({
  productos,
  cambiarProductos,
  analizar,
  ocupado,
  error,
  urlDeLaPlantilla,
}: {
  analizar: (f: File) => void
  cambiarProductos: (p: ProductosDelAlta | null) => void
  error: null | string
  ocupado: boolean
  productos: ProductosDelAlta | null
  urlDeLaPlantilla: null | string
}) => (
  <Seccion
    descripcion="Opcional. Sube un Excel con tus productos —nuestra plantilla o el tuyo— y confirma qué columna va a qué campo. Se importan como borradores al montar la web, con sus categorías, antes de los menús y del diseño; también puedes hacerlo después desde Productos → Importar."
    titulo="Tus productos"
  >
    {error && (
      <div className="mb-3">
        <Aviso tono="error">{error}</Aviso>
      </div>
    )}
    {productos ? (
      <>
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <p className="text-[13px] font-medium">
            {productos.archivo.name} · {productos.analisis.totalDeFilas} {productos.analisis.totalDeFilas === 1 ? 'fila' : 'filas'}
          </p>
          <button className="text-[13px] text-violeta hover:underline" onClick={() => cambiarProductos(null)} type="button">
            Quitar el fichero
          </button>
        </div>
        <div className="mt-4">
          <ComoQuedaran analisis={productos.analisis} mapeo={productos.mapeo} />
        </div>
        <TablaDeMapeo analisis={productos.analisis} cambiar={(mapeo) => cambiarProductos({ ...productos, mapeo })} mapeo={productos.mapeo} />
      </>
    ) : (
      <>
        <SelectorDeArchivo alElegir={analizar} ocupado={ocupado} />
        <div className="mt-3 flex flex-wrap items-center gap-3 text-[13px] text-tenue">
          {urlDeLaPlantilla ? (
            <a className="font-medium text-violeta hover:underline" href={urlDeLaPlantilla}>
              Bajar la plantilla Excel
            </a>
          ) : (
            <span>La plantilla Excel se puede bajar desde Productos → Importar en cuanto exista la web.</span>
          )}
          {ocupado && <span role="status">Leyendo el Excel…</span>}
        </div>
        <p className="mt-4 text-[13px] text-tenue">Sin productos por ahora también se puede continuar: la web nace igual y los importas cuando quieras.</p>
      </>
    )}
  </Seccion>
)

// --- 7. Resumen ----------------------------------------------------------------------------

const Fila = ({ etiqueta, children }: { children: ReactNode; etiqueta: string }) => (
  <div className="flex flex-col gap-0.5 border-b border-linea py-2.5 last:border-b-0 md:flex-row md:gap-4">
    <dt className="w-44 shrink-0 text-[13px] text-tenue">{etiqueta}</dt>
    <dd className="text-[13px]">{children}</dd>
  </div>
)

export const PasoResumen = ({
  datos,
  ficheros,
  plantilla,
  tipo,
  productos,
  saldo = null,
}: {
  datos: Datos
  ficheros: Ficheros
  plantilla: Plantilla | undefined
  productos: ProductosDelAlta | null
  /** Los créditos que tiene la cuenta: la IA del alta los gasta, y se dice antes de crear. */
  saldo?: null | number
  tipo: TipoDeWeb | null
}) => {
  const paginas = plantilla ? plantilla.paginas.filter((p) => p.obligatoria || datos.paginas.includes(p.clave)) : []
  const servicios = datos.servicios.split('\n').map((s) => s.trim()).filter(Boolean)
  const imagenes = Object.values(ficheros.imagenes).flat().length + ficheros.sueltas.length + [ficheros.logo, ficheros.logoEnOscuro, ficheros.favicon].filter(Boolean).length
  return (
    <Seccion descripcion="Esto es lo que se va a crear. La web nace sin publicar, con sus páginas en borrador, para que la revises." titulo="Resumen">
      <dl>
        {tipo === 'tienda' && <Fila etiqueta="Productos">{productos ? `${productos.analisis.totalDeFilas} filas de «${productos.archivo.name}», como borradores` : 'Ninguno por ahora: se importan después desde Productos'}</Fila>}
        <Fila etiqueta="Tipo">{tipo === 'tienda' ? `Tienda · ${datos.tienda.modo === 'catalogo' ? 'catálogo (Consultar)' : 'venta en línea'} · ${datos.tienda.moneda}` : 'Informativa'}</Fila>
        <Fila etiqueta="Plantilla">{plantilla?.nombre ?? '—'}</Fila>
        <Fila etiqueta="Nombre y dirección">
          {datos.nombre} · {datos.slug}.{marca()?.dominioWebs ?? DOMINIO_DE_LA_PLATAFORMA}{datos.dominio ? ` · ${datos.dominio}` : ''}
        </Fila>
        <Fila etiqueta="Marca">
          {[datos.marca.primario, datos.marca.secundario, datos.marca.acento].filter(Boolean).map((c) => (
            <span className="mr-2 inline-flex items-center gap-1" key={c}>
              <span className="inline-block size-3 rounded-full border border-linea" style={{ background: c }} /> {c}
            </span>
          ))}
          {TIPOGRAFIAS.find((t) => t.valor === datos.marca.tipografia)?.nombre}
        </Fila>
        <Fila etiqueta="Contacto">{[datos.contacto.correo, datos.contacto.telefono].filter(Boolean).join(' · ') || 'Sin datos todavía'}</Fila>
        {datos.contacto.horario.trim() && <Fila etiqueta="Horario">{datos.contacto.horario.trim().split(/\r?\n/).filter((l) => l.trim()).join(' · ')}</Fila>}
        <Fila etiqueta="Datos legales">{[datos.fiscal.razonSocial, datos.fiscal.identificacion].filter(Boolean).join(' · ') || 'Sin datos: las legales quedarán con huecos por rellenar'}</Fila>
        <Fila etiqueta="Brief">
          {datos.brief.trim() ? `${datos.brief.trim().split(/\s+/).length} palabras` : 'Sin brief'}
          {ficheros.documentos.length > 0 ? ` · ${ficheros.documentos.length} ${ficheros.documentos.length === 1 ? 'documento' : 'documentos'}` : ''}
          {!datos.brief.trim() && ficheros.documentos.length === 0 ? ' — los textos nacerán por rellenar' : ' — con esto se redactan los textos'}
        </Fila>
        <Fila etiqueta="Páginas">{paginas.map((p) => p.titulo).join(' · ')}{servicios.length > 0 ? ` · ${servicios.length} ${servicios.length === 1 ? 'servicio' : 'servicios'}` : ''}</Fila>
        <Fila etiqueta="Imágenes">{imagenes === 0 ? 'Ninguna por ahora' : `${imagenes} ${imagenes === 1 ? 'imagen' : 'imágenes'}`}</Fila>
        <Fila etiqueta="Créditos de IA">
          {saldo === null ? 'La IA escribe los textos y afina el diseño con tus créditos.' : `La IA escribe los textos y afina el diseño con tus créditos: tienes ${creditosEnLlano(saldo)}.`} Antes de empezar te diremos cuánto cuesta; si no te llegan, podrás seguir sin IA.
        </Fila>
        <Fila etiqueta="Coste">Incluido en tu plan.</Fila>
      </dl>
    </Seccion>
  )
}
